/** Вкладка «Настройки» раздела «Заказы»: справочник артикулов, каталоги, кластеры,
 * цвета, журнал действий, admin-импорт старого дашборда. */
import React, { useState } from "react";

import type { OrdersMatrixReport } from "@/api/orders";
import { useAuth } from "@/auth/useAuth";
import { cn } from "@/lib/utils";
import { useOrdersEdit } from "../useOrdersEdit";
import { ActionLogView } from "./ActionLogView";
import { AttrsEditor } from "./AttrsEditor";
import { CatalogsEditor } from "./CatalogsEditor";
import { ClustersEditor } from "./ClustersEditor";
import { ColorsEditor } from "./ColorsEditor";
import { ImportPanel } from "./ImportPanel";

type Section = "attrs" | "catalogs" | "clusters" | "colors" | "log" | "import";

const SECTIONS: { value: Section; label: string; adminOnly?: boolean }[] = [
  { value: "attrs", label: "Справочник артикулов" },
  { value: "catalogs", label: "Каталоги" },
  { value: "clusters", label: "Кластеры складов" },
  { value: "colors", label: "Цвета" },
  { value: "log", label: "Журнал" },
  { value: "import", label: "Импорт", adminOnly: true },
];

export function SettingsTab({
  report,
}: {
  report: OrdersMatrixReport | undefined;
}): React.ReactElement {
  const { user } = useAuth();
  const canEdit = useOrdersEdit();
  const [section, setSection] = useState<Section>("attrs");
  const visible = SECTIONS.filter((s) => !s.adminOnly || user?.is_admin);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {visible.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setSection(s.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs transition-colors",
              section === s.value
                ? "border-primary bg-primary/10 font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {s.label}
          </button>
        ))}
        {!canEdit && (
          <span className="ml-auto self-center text-[11px] text-muted-foreground">
            Только просмотр — нет права «Заказы: редактирование»
          </span>
        )}
      </div>

      {section === "attrs" && <AttrsEditor report={report} canEdit={canEdit} />}
      {section === "catalogs" && <CatalogsEditor canEdit={canEdit} />}
      {section === "clusters" && <ClustersEditor canEdit={canEdit} />}
      {section === "colors" && <ColorsEditor canEdit={canEdit} />}
      {section === "log" && <ActionLogView />}
      {section === "import" && <ImportPanel />}
    </div>
  );
}
